import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Check, Loader2 } from "lucide-react";

const fmt = (sym, v) => `${sym || "$"}${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("payment_intent") || params.get("session_id");
  const redirectStatus = params.get("redirect_status");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    if (redirectStatus === "failed") {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
      } catch (e) {}
      attempts += 1;
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="pt-40 pb-40 max-w-[700px] mx-auto px-6 text-center min-h-[70vh]">
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="animate-spin mx-auto mb-8 text-white/60" size={40} strokeWidth={1.3} />
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/50">Confirming your order…</p>
        </div>
      )}

      {status === "paid" && (
        <div data-testid="payment-success" className="reveal">
          <div className="w-16 h-16 border border-white flex items-center justify-center mx-auto mb-10">
            <Check strokeWidth={1.3} size={28} />
          </div>
          <p className="font-mono-label text-xs uppercase tracking-luxe text-white/40 mb-4">Order Confirmed</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-light mb-4">Thank you</h1>
          {order?.order_number && (
            <p data-testid="order-number" className="font-mono-label text-sm text-white/70 mb-8">
              Order {order.order_number}
            </p>
          )}
          <p className="text-white/60 mb-10">
            Your order has been received and is being prepared with care. A confirmation has been sent to your email.
          </p>
          {order?.items?.length > 0 && (
            <div className="border-t border-white/10 pt-8 mb-10 text-left max-w-sm mx-auto">
              {order.items.map((it, k) => (
                <div key={k} className="flex justify-between text-sm text-white/70 py-2">
                  <span>{it.name} · {it.size} × {it.quantity}</span>
                  <span className="font-mono-label">{fmt(order.symbol, it.line_total ?? it.price * it.quantity)}</span>
                </div>
              ))}
              <div className="flex justify-between text-white/60 text-sm pt-3 border-t border-white/10 mt-2">
                <span>Subtotal</span>
                <span className="font-mono-label">{fmt(order.symbol, order.subtotal)}</span>
              </div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-green-400 text-sm pt-1">
                  <span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span>
                  <span className="font-mono-label">-{fmt(order.symbol, order.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-white/60 text-sm pt-1">
                <span>Shipping</span>
                <span className="font-mono-label">{order.shipping_fee === 0 ? "Free" : fmt(order.symbol, order.shipping_fee)}</span>
              </div>
              <div className="flex justify-between text-white pt-3 mt-1 border-t border-white/10">
                <span className="font-mono-label text-xs uppercase tracking-luxe">Total</span>
                <span className="font-mono-label">{fmt(order.symbol, order.amount)} {(order.currency || "").toUpperCase()}</span>
              </div>
            </div>
          )}
          <Link to="/collection" className="inline-block border border-white text-white px-10 py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-white hover:text-black">
            Continue Shopping
          </Link>
        </div>
      )}

      {(status === "failed" || status === "timeout") && (
        <div data-testid="payment-failed">
          <h1 className="font-serif-display text-4xl font-light mb-6">
            {status === "timeout" ? "Still processing" : "Something went wrong"}
          </h1>
          <p className="text-white/60 mb-10">
            {status === "timeout"
              ? "Your payment is taking longer than expected. Check your email for confirmation."
              : "We couldn't confirm your payment. Please try again."}
          </p>
          <Link to="/cart" className="inline-block border border-white text-white px-10 py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-white hover:text-black">
            Return to Bag
          </Link>
        </div>
      )}
    </div>
  );
}
